import React, { useState, useEffect, useRef } from "react"; // Import useRef
import { ChevronDown, ChevronRight, CheckCircle } from "lucide-react";
import { useSelector } from "react-redux";
import { getQuoteChecklistDetails, updateQuoteStageAndTasks } from "../../../Api";
import { showSwal } from "../../../Helper/ShowAlert";
import { TRANSPORTERS } from "../../../Helper/partnerLabels";

// Assuming STAGES and CHECKLIST_TASKS are globally consistent with your backend model
export const STAGES = {
  QUERY_STAGE: "query_stage",
  QUOTE_STAGE: "quote_stage",
  NOMINATION_STAGE: "nomination_stage",
  COLLECTION_STAGE: "collection_stage",
  AIRLINE_BOOKING_STAGE: "airline_booking_stage",
  DOCUMENTATION_STAGE: "documentation_stage",
  HANDOVER_STAGE: "handover_stage",
  POST_DELIVERY_STAGE: "post_delivery_stage",
  INVOICING_BILLING_STAGE: "invoicing_billing_stage",
  COMPLETED: "completed",
  CANCELLED: "cancelled",
};

export const CHECKLIST_TASKS = {
  [STAGES.QUERY_STAGE]: [
    { id: "QUERY_STAGE_ACKNOWLEDGE_QUERY", label: "Acknowledge Customer Query" },
    { id: "QUERY_STAGE_GATHER_MISSING_INFO", label: "Gather Missing Information" },
    // { id: "QUERY_STAGE_CONTACT_VENDORS", label: "Contact Vendors for Initial Rates" },
    // { id: "QUERY_STAGE_CALCULATE_COSTS", label: "Calculate Initial Costs" },
    { id: "QUERY_STAGE_SHIPPER_DETAIL_REQUEST", label: "Gather Shipper Details From Customer" },
    { id: "QUERY_STAGE_COORDINATE_CUSTOMER_DETAILS", label: "Co-ordinate with customer for their details" },
  ],
  [STAGES.QUOTE_STAGE]: [
    { id: "QUOTE_STAGE_SEND_AIRLINE_RATE_REQUESTS", label: "Send rate requests to Airlines" },
    { id: "QUOTE_STAGE_SEND_HAULER_RATE_REQUESTS", label: `Send rate requests to ${TRANSPORTERS}` },
    { id: "QUOTE_STAGE_PREPARE_FINAL_QUOTE_COMPARISON", label: "Prepare final quote cost comparison table" },
    { id: "QUOTE_STAGE_SEND_FINAL_QUOTE", label: "Send final quote to customer" },
  ],
  [STAGES.NOMINATION_STAGE]: [
    { id: "NOMINATION_STAGE_NOMINATE_AN_OPTION", label: "Nominate an option received from the customer" },
    // { id: "NOMINATION_STAGE_GET_CONFIRMATION", label: "Confirmation for the selected option" },
    { id: "NOMINATION_STAGE_GATHER_SHIPPER_DOCS", label: "Gather Shipper Documents" },
    { id: "NOMINATION_STAGE_VERIFY_EORI_LICENSES", label: "Verify EORI/Export Licenses" },
    { id: "NOMINATION_STAGE_CONFIRM_COLLECTION_DETAILS", label: "Confirm Collection Details with Shipper" },
  ],
  [STAGES.COLLECTION_STAGE]: [
    { id: "COLLECTION_STAGE_GENERATE_COLLECTION_NOTE", label: "Generate Collection Note" },
    { id: "COLLECTION_STAGE_DISPATCH_TRANSPORTER", label: "Dispatch Transporter for Collection" },
    { id: "COLLECTION_STAGE_CONFIRM_COLLECTION", label: "Confirm Item Collection" },
    { id: "COLLECTION_STAGE_RECEIVE_GRN", label: "Receive Goods Receipt Note (GRN)" },
  ],
  [STAGES.AIRLINE_BOOKING_STAGE]: [
    { id: "AIRLINE_BOOKING_STAGE_CHECK_FLIGHT_SCHEDULES", label: "Check Flight Schedules" },
    { id: "AIRLINE_BOOKING_STAGE_BOOK_FLIGHT", label: "Book Airline Flight" },
    { id: "AIRLINE_BOOKING_STAGE_RECEIVE_BOOKING_CONFIRMATION", label: "Receive Booking Confirmation" },
  ],
  [STAGES.DOCUMENTATION_STAGE]: [
    { id: "DOCUMENTATION_STAGE_DRAFT_AWB_MANIFEST", label: "Draft AWB & Manifest" },
    { id: "DOCUMENTATION_STAGE_SEND_AWB_DRAFT_FOR_APPROVAL", label: "Send AWB Draft for Approval" },
    { id: "DOCUMENTATION_STAGE_GET_AWB_APPROVED", label: "Get AWB Approved" },
    { id: "DOCUMENTATION_STAGE_FINAL_AWB_PRINT", label: "Print Final AWB" },
    { id: "DOCUMENTATION_STAGE_PREPARE_CUSTOMS_DOCS", label: "Prepare Customs Documents" },
  ],
  [STAGES.HANDOVER_STAGE]: [
    { id: "HANDOVER_STAGE_DELIVER_TO_GHA", label: "Deliver Goods to GHA" },
    { id: "HANDOVER_STAGE_CONFIRM_GHA_RECEIPT", label: "Confirm GHA Receipt" },
    { id: "HANDOVER_STAGE_INITIATE_CUSTOMS_CLEARANCE", label: "Initiate Customs Clearance" },
    { id: "HANDOVER_STAGE_MONITOR_CUSTOMS_STATUS", label: "Monitor Customs Status" },
  ],
  [STAGES.POST_DELIVERY_STAGE]: [
    { id: "POST_DELIVERY_STAGE_TRACK_FLIGHT", label: "Track Flight" },
    { id: "POST_DELIVERY_STAGE_CONFIRM_DEPARTURE", label: "Confirm Departure" },
    { id: "POST_DELIVERY_STAGE_MONITOR_TRANSIT", label: "Monitor Transit" },
    { id: "POST_DELIVERY_STAGE_RESOLVE_SNAGS", label: "Resolve Any Snags" },
    { id: "POST_DELIVERY_STAGE_SEND_FINAL_PRE_ALERT", label: "Send Final Pre-Alert" },
  ],
  [STAGES.INVOICING_BILLING_STAGE]: [
    { id: "INVOICING_BILLING_STAGE_GENERATE_INVOICE", label: "Generate Invoice" },
    { id: "INVOICING_BILLING_STAGE_SEND_INVOICE", label: "Send Invoice to Customer" },
    { id: "INVOICING_BILLING_STAGE_MONITOR_PAYMENT", label: "Monitor Payment Status" },
    { id: "INVOICING_BILLING_STAGE_CONFIRM_PAYMENT", label: "Confirm Payment Received" },
  ],
  [STAGES.COMPLETED]: [
    { id: "COMPLETED_SHIPMENT_FINALIZED", label: "Shipment Finalized" },
  ],
  [STAGES.CANCELLED]: [
    { id: "CANCELLED_SHIPMENT_TERMINATED", label: "Shipment Terminated" },
  ],
};


function StageChecklist({ pT, quoteId, setCurrentStage, defaultOpen }) {
  const [isOpen, setIsOpen] = useState(false);
  const loginToken = useSelector((state) => state.reducer.userToken);

  const userLoggedIn = useSelector((state) => state.reducer.userLoggedIn);
  const [quoteData, setQuoteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const initialCompletedTasksRef = useRef([]);

  const fetchChecklist = async () => {
    if (!quoteId || !loginToken) {
      setLoading(false);
      setError("Quote ID or login token is missing.");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await getQuoteChecklistDetails({ token: loginToken, quoteId, role: userLoggedIn?.role });
      if (response.status === 200 && response.checklist) {
        setQuoteData(response.checklist);
        setCurrentStage(response.checklist?.currentStage)
        initialCompletedTasksRef.current = [...response.checklist.completedTasks];
        setHasChanges(false);
      } else {
        setError(response.message || "Failed to fetch checklist details.");
        setQuoteData(null);
      }
    } catch (err) {
      console.error("Error fetching checklist details:", err);
      setError("An error occurred while fetching checklist details.");
      setQuoteData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChecklist();
  }, [quoteId, loginToken]);

  const currentStageChecklist = quoteData ? CHECKLIST_TASKS[quoteData.currentStage] || [] : [];

  const formatStageName = (stage) => {
    return stage.replace(/_/g, ' ').split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const allTasksCompleted = currentStageChecklist.length > 0 &&
    currentStageChecklist.every(task => quoteData.completedTasks.includes(task.id));

  const handleCheckboxChange = (taskId) => {
    setQuoteData(prevQuoteData => {
      const currentCompletedTasks = prevQuoteData.completedTasks || [];
      const isCurrentlyChecked = currentCompletedTasks.includes(taskId);

      const newCompletedTasks = isCurrentlyChecked
        ? currentCompletedTasks.filter(id => id !== taskId)
        : [...currentCompletedTasks, taskId];

      const initialTasksSet = new Set(initialCompletedTasksRef.current);
      const newTasksSet = new Set(newCompletedTasks);

      const changed = newCompletedTasks.length !== initialCompletedTasksRef.current.length ||
        ![...newTasksSet].every(item => initialTasksSet.has(item));

      setHasChanges(changed);

      return {
        ...prevQuoteData,
        completedTasks: newCompletedTasks,
      };
    });
  };

  const handleUpdateChecklist = async () => {
    if (!quoteData || !loginToken || isUpdating || !hasChanges) return;

    setIsUpdating(true);
    try {
      const response = await updateQuoteStageAndTasks({
        token: loginToken,
        quoteId: quoteId,
        role: userLoggedIn?.role,
        data: {
          taskIds: quoteData.completedTasks,
        },
      });

      if (response.status === 200) {
        showSwal("Success", "Checklist updated successfully!", 200);
        fetchChecklist();
      } else {
        showSwal("Error", response.message || "Failed to update checklist.", response.status || 500);
      }
    } catch (err) {
      console.error("Error updating checklist:", err);
      showSwal("Error", "An error occurred while updating checklist.", 500);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleNextStage = async () => {
    if (!quoteData || !loginToken || isUpdating || !allTasksCompleted) return;

    const allStages = Object.values(STAGES);
    const currentIndex = allStages.indexOf(quoteData.currentStage);
    const nextStage = allStages[currentIndex + 1];

    if (!nextStage) {
      showSwal("Info", "This is the last stage or no next stage defined!", "info");
      return;
    }

    setIsUpdating(true);
    try {
      const response = await updateQuoteStageAndTasks({
        token: loginToken,
        quoteId: quoteId,
        role: userLoggedIn?.role,
        data: {
          newStage: nextStage,
        },
      });

      if (response.status === 200) {
        showSwal("Success", `Moved to ${formatStageName(nextStage)}!`, 200);
        fetchChecklist();
      } else {
        showSwal("Error", response.message || "Failed to move to next stage.", response.status || 500);
      }
    } catch (err) {
      console.error("Error moving to next stage:", err);
      showSwal("Error", "An error occurred while moving to the next stage.", 500);
    } finally {
      setIsUpdating(false);
    }
  };


  if (loading) {
    return (
      <div className="bg-gray-600 dark:bg-[oklch(0.3_0.024_264)] rounded-lg overflow-hidden shadow-lg max-w-md mx-auto my-8 font-inter text-white p-4 text-center transition-colors duration-300">
        Loading Stage Checklist...
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-600 dark:bg-red-700 rounded-lg overflow-hidden shadow-lg max-w-md mx-auto my-8 font-inter text-white p-4 text-center transition-colors duration-300">
        Error: {error}
      </div>
    );
  }

  if (!quoteData) {
    return (
      <div className="bg-gray-600 dark:bg-[oklch(0.3_0.024_264)] rounded-lg overflow-hidden shadow-lg max-w-md mx-auto my-8 font-inter text-white p-4 text-center transition-colors duration-300">
        No checklist data available for this quote.
      </div>
    );
  }

  return (
    <div className={`bg-white dark:bg-[oklch(0.26_0.028_264)] rounded-xl overflow-hidden shadow-sm border border-gray-200 dark:border-[oklch(0.33_0.022_264)] font-inter transition-colors duration-300 ${pT ? 'my-8 mx-auto max-w-md' : 'my-0'}`}>
      <>
        <div
          className={`bg-white dark:bg-[oklch(0.26_0.028_264)] text-gray-900 dark:text-white ${pT ? 'px-6 py-4' : 'px-6 py-4'} flex items-center justify-between cursor-pointer transition-colors duration-300`}
          onClick={() => setIsOpen(!isOpen)}
        >
          <h3 className="font-medium text-lg">
            Stage Checklist: <span className="font-bold">{formatStageName(quoteData.currentStage)}</span>
          </h3>
          <div className="flex items-center space-x-2">
            <div
              className={`w-2 h-2 rounded-full ${currentStageChecklist.length > 0 && quoteData.completedTasks.length === currentStageChecklist.length ? 'bg-green-500' : 'bg-gray-400'}`}
              title="All tasks completed"
            />
            <div
              className={`w-2 h-2 rounded-full ${quoteData.completedTasks.length > 0 && quoteData.completedTasks.length < currentStageChecklist.length ? 'bg-yellow-500' : 'bg-gray-400'}`}
              title="Some tasks completed"
            />
            <div
              className={`w-2 h-2 rounded-full ${quoteData.completedTasks.length === 0 && currentStageChecklist.length > 0 ? 'bg-red-500' : 'bg-gray-400'}`}
              title="No tasks completed"
            />
            <div className={(isOpen ? "rotate-90" : "") + " duration-300"}>
              <ChevronRight size={18} className="ml-2 text-gray-600 dark:text-gray-300" />
            </div>
          </div>
        </div>
        <div
          className={`transition-all duration-300 ease-in-out overflow-hidden ${isOpen
            ? "max-h-[600px] opacity-100"
            : "max-h-0 opacity-0"
            }`}
        >
          <div className="bg-white dark:bg-[oklch(0.26_0.028_264)] p-5 space-y-4 rounded-b-lg transition-colors duration-300">
            {currentStageChecklist.length > 0 ? (
              currentStageChecklist.map((item) => {
                const isChecked = quoteData.completedTasks.includes(item.id);
                return (
                  <div key={item.id} className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => handleCheckboxChange(item.id)}
                      className="accent-blue-500 w-4 h-4 rounded focus:ring-blue-500 cursor-pointer"
                    />
                    <span
                      className={`text-base ${isChecked
                        ? "text-gray-600 dark:text-gray-400 font-medium line-through"
                        : "text-gray-800 dark:text-gray-200"
                        }`}
                    >
                      {item.label}
                    </span>
                    {isChecked && <CheckCircle size={18} className="text-blue-500" />}
                  </div>
                );
              })
            ) : (
              <p className="text-gray-600 dark:text-gray-400">No checklist tasks defined for this stage.</p>
            )}
            <div className="flex justify-end space-x-4 mt-4">
              <button
                onClick={allTasksCompleted ? handleNextStage : handleUpdateChecklist}
                disabled={isUpdating || !hasChanges}
                className={`px-4 py-2 rounded-md text-white font-semibold transition-colors duration-200 ${(isUpdating || !hasChanges)
                  ? "bg-gray-400 dark:bg-[oklch(0.38_0.022_264)] cursor-not-allowed"
                  : allTasksCompleted
                    ? "bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-800"
                    : "bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800"
                  }`}
              >
                {isUpdating ? "Updating..." : !allTasksCompleted ? "Update Checklist" : "Next Stage"}
              </button>
            </div>
          </div>
        </div>
      </>
    </div>
  );
}

export default StageChecklist;
